/**
 * Shared rate math aligned with BDFCreatorContainer (createBDF) when no package rate:
 * monthToDurationRate — amount from specialRatePerMonth + date span (DD-MM-YYYY).
 */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export function normalizeRateRecord(inv) {
  return {
    ...inv,
    ratePerMonth: Number(inv.ratePerMonth ?? inv.rpm ?? 0),
    specialRatePerMonth: Number(inv.specialRatePerMonth ?? 0),
  };
}

export function monthToDurationRate(ele, startDateStr, endDateStr) {
  const startDayjs = dayjs(startDateStr, "DD-MM-YYYY", true);
  const endDayjs = dayjs(endDateStr, "DD-MM-YYYY", true);

  if (!startDayjs.isValid() || !endDayjs.isValid()) {
    console.error("Invalid start or end date passed to monthToDurationRate");
    return { newSpecialRate: 0, discount: 0 };
  }

  const differenceInDays = endDayjs.diff(startDayjs, "day") + 1;
  if (differenceInDays < 1) {
    return { newSpecialRate: 0, discount: 0 };
  }

  const monthsDiff = endDayjs.diff(startDayjs, "month", true);

  const isStartFirstDay = startDayjs.date() === 1;
  const isEndLastDay = endDayjs.date() === endDayjs.daysInMonth();

  const roundedMonthsDiff = Math.round(monthsDiff);
  const isWholeMonthPeriod =
    Math.abs(monthsDiff - roundedMonthsDiff) < 0.1 && roundedMonthsDiff >= 1;

  const calculateExpectedDays = (months) => {
    let expectedDays = 0;
    let currentMonth = startDayjs;

    for (let i = 0; i < months; i++) {
      expectedDays += currentMonth.daysInMonth();
      currentMonth = currentMonth.add(1, "month");
    }

    return expectedDays;
  };

  const expectedDaysForRoundedMonths =
    calculateExpectedDays(roundedMonthsDiff);
  const maxExtraDays = Math.min(
    6,
    Math.ceil(expectedDaysForRoundedMonths * 0.1),
  );

  const isClearWholeMonthPeriod =
    roundedMonthsDiff >= 2 && Math.abs(monthsDiff - roundedMonthsDiff) <= 0.15;

  const adjustedMaxExtraDays = isClearWholeMonthPeriod
    ? Math.max(maxExtraDays, 3)
    : maxExtraDays;

  const isVeryCloseToWholeMonths =
    roundedMonthsDiff >= 2 &&
    Math.abs(monthsDiff - roundedMonthsDiff) <= 0.25 &&
    differenceInDays >= expectedDaysForRoundedMonths - 4 &&
    differenceInDays <= expectedDaysForRoundedMonths;

  const isDynamicMonthPeriod =
    roundedMonthsDiff >= 2 &&
    differenceInDays >= expectedDaysForRoundedMonths - 4 &&
    differenceInDays <= expectedDaysForRoundedMonths;

  const calculateDynamicTolerance = (months) => {
    let minDays = 0;
    let maxDays = 0;
    let currentMonth = startDayjs;

    for (let i = 0; i < months; i++) {
      minDays += 28;
      currentMonth = currentMonth.add(1, "month");
    }

    currentMonth = startDayjs;
    for (let i = 0; i < months; i++) {
      maxDays += 31;
      currentMonth = currentMonth.add(1, "month");
    }

    return { minDays, maxDays };
  };

  const tolerance = calculateDynamicTolerance(roundedMonthsDiff);
  const isDynamicMonthPeriodWithTolerance =
    roundedMonthsDiff >= 2 &&
    differenceInDays >= tolerance.minDays &&
    differenceInDays <= tolerance.maxDays;

  const isActuallyWholeMonths =
    isWholeMonthPeriod &&
    ((isStartFirstDay && isEndLastDay) ||
      startDayjs.date() === endDayjs.date() ||
      endDayjs.date() === startDayjs.date() - 1 ||
      (roundedMonthsDiff >= 2 && Math.abs(monthsDiff - roundedMonthsDiff) < 0.05) ||
      (roundedMonthsDiff >= 2 &&
        Math.abs(monthsDiff - roundedMonthsDiff) <= 0.2 &&
        differenceInDays >=
          expectedDaysForRoundedMonths - adjustedMaxExtraDays &&
        differenceInDays <= expectedDaysForRoundedMonths) ||
      isVeryCloseToWholeMonths ||
      isDynamicMonthPeriod ||
      isDynamicMonthPeriodWithTolerance);

  let newSpecialRate = 0;
  let discount = 0;

  const daysInStartMonth = startDayjs.daysInMonth();
  const dailyRate = ele?.specialRatePerMonth / daysInStartMonth;
  const isFebruary = startDayjs.month() === 1;

  if (isStartFirstDay && isEndLastDay) {
    const fullMonths = Math.round(monthsDiff);
    newSpecialRate = ele?.specialRatePerMonth * fullMonths;
  } else if (isActuallyWholeMonths) {
    newSpecialRate = ele?.specialRatePerMonth * roundedMonthsDiff;
  } else if (differenceInDays % 30 === 0) {
    const fullMonths = differenceInDays / 30;
    newSpecialRate = ele?.specialRatePerMonth * fullMonths;
  } else if (isStartFirstDay && monthsDiff >= 1) {
    const fullMonths = Math.floor(monthsDiff);
    const remainingDays = differenceInDays - fullMonths * 30;
    newSpecialRate =
      ele?.specialRatePerMonth * fullMonths + remainingDays * dailyRate;
  } else if (
    isFebruary &&
    (differenceInDays === 27 ||
      differenceInDays === 28 ||
      differenceInDays === 29)
  ) {
    newSpecialRate = ele?.specialRatePerMonth;
  } else {
    newSpecialRate = Math.round((ele?.specialRatePerMonth / 30) * differenceInDays);
  }

  const perDayRpm = ele?.ratePerMonth / daysInStartMonth;
  const srPerDay = ele?.specialRatePerMonth / daysInStartMonth;
  discount = Math.floor(Number(100 - (srPerDay * 100) / perDayRpm));

  return { newSpecialRate: Math.floor(newSpecialRate), discount };
}
